import {
  AttachMoney,
  EventRepeat as EventRepeatIcon,
  Receipt,
  TrendingUp,
} from '@mui/icons-material';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { getBusinessCrossInvoicesSummary } from 'lib/api';
import { AutocompleteInput, SelectInput } from 'lib/common/Inputs';
import TableCard from 'lib/designSystem/Card/TableCard';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import MultiSelectCheckbox from 'lib/designSystem/MultiSelectCheckbox';
import PageHeader from 'lib/designSystem/PageHeader';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import EnhancedTable from 'lib/designSystem/Table';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { CHART_COLORS } from 'lib/functions/const/colors';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import type { MonthTableShortcut } from './constants';
import {
  DEFAULT_PERIOD,
  MONTH_TABLE_OPTIONS,
  PERIOD_OPTIONS,
} from './constants';
import type { CrossInvoiceItem, CrossInvoicesData, Totals } from './types';
import type { PeriodOption } from './types';
import {
  calculateTotals,
  getFilteredChartData,
  getFilteredTableData,
  processChartData,
} from './utils';

const CHART_SERIES_COLORS = [
  CHART_COLORS.blue,
  CHART_COLORS.orange,
  CHART_COLORS.green,
];

const INITIAL_TOTALS: Totals = {
  totalCrossAmount: 0,
  totalEmittedAmount: 0,
  totalReceivedAmount: 0,
  averageCrossAmount: 0,
  averageEmittedAmount: 0,
  averageReceivedAmount: 0,
};

const BusinessCrossInvoices: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [crossInvoicesData, setCrossInvoicesData] = useState<CrossInvoicesData>(
    {
      dataPayers: { total: [] },
      monthsMetadata: [],
    },
  );
  const [chartData, setChartData] = useState<
    ReturnType<typeof processChartData>
  >([]);
  const [totals, setTotals] = useState<Totals>(INITIAL_TOTALS);
  const [selectedPeriod, setSelectedPeriod] =
    useState<PeriodOption>(DEFAULT_PERIOD);
  const [selectedBusinessFilter, setSelectedBusinessFilter] =
    useState<CrossInvoiceItem | null>(null);
  const [filteredBusinessTable, setFilteredBusinessTable] = useState<
    CrossInvoiceItem[]
  >([]);
  const [monthTableShortcut, setMonthTableShortcut] =
    useState<MonthTableShortcut>('');
  const [useMonthShortcuts, setUseMonthShortcuts] = useState(true);
  const [selectedMonthsManual, setSelectedMonthsManual] = useState<string[]>(
    [],
  );

  const filteredChartData = useMemo(
    () => getFilteredChartData(chartData, selectedPeriod),
    [chartData, selectedPeriod],
  );

  useEffect(() => {
    const fetchRelations = async () => {
      if (!id) return;
      const crossInvoices = await getBusinessCrossInvoicesSummary(dispatch, id);
      const raw = crossInvoices as CrossInvoicesData | null | undefined;
      const data: CrossInvoicesData = {
        dataPayers:
          raw?.dataPayers && typeof raw.dataPayers === 'object'
            ? {
                ...raw.dataPayers,
                total: Array.isArray(raw.dataPayers.total)
                  ? raw.dataPayers.total
                  : [],
              }
            : { total: [] },
        monthsMetadata: Array.isArray(raw?.monthsMetadata)
          ? raw.monthsMetadata
          : [],
        totalEmittedAmount: raw?.totalEmittedAmount,
        totalRecievedAmount: raw?.totalRecievedAmount,
        totalReceivedOverEmittedPercent: raw?.totalReceivedOverEmittedPercent,
      };
      setCrossInvoicesData(data);
      setChartData(processChartData(data));
      setLoading(false);
    };
    void fetchRelations();
  }, [id, dispatch]);

  useEffect(() => {
    if (chartData.length > 0) {
      const nextTotals = calculateTotals(filteredChartData);
      setTotals(nextTotals);
    }
  }, [chartData, filteredChartData]);

  const availableMonths = useMemo(() => {
    const keys = Object.keys(crossInvoicesData.dataPayers ?? {}).filter(
      (key) => key !== 'total',
    );
    return keys.sort((a, b) => {
      const dateA = new Date(`${a}-01`).getTime();
      const dateB = new Date(`${b}-01`).getTime();
      return dateB - dateA;
    });
  }, [crossInvoicesData.dataPayers]);

  const selectedMonthsFromShortcut = useMemo(() => {
    if (monthTableShortcut === '') return availableMonths;
    const n = parseInt(monthTableShortcut, 10);
    return availableMonths.slice(0, n);
  }, [monthTableShortcut, availableMonths]);

  const selectedMonthsTable = useMemo(
    () =>
      useMonthShortcuts ? selectedMonthsFromShortcut : selectedMonthsManual,
    [useMonthShortcuts, selectedMonthsFromShortcut, selectedMonthsManual],
  );

  useEffect(() => {
    const tableData = getFilteredTableData(
      crossInvoicesData,
      selectedMonthsTable,
      selectedBusinessFilter,
    );
    setFilteredBusinessTable(tableData);
  }, [crossInvoicesData, selectedMonthsTable, selectedBusinessFilter]);

  const handleChangeBusiness = useCallback(
    (newValue: CrossInvoiceItem | null) => {
      setSelectedBusinessFilter(newValue);
      setChartData(processChartData(crossInvoicesData, newValue));
    },
    [crossInvoicesData],
  );

  const handleToggleMonthMode = useCallback(() => {
    if (useMonthShortcuts) {
      setSelectedMonthsManual(selectedMonthsFromShortcut);
    } else {
      setMonthTableShortcut('');
    }
    setUseMonthShortcuts((prev) => !prev);
  }, [useMonthShortcuts, selectedMonthsFromShortcut]);

  const availableBusinesses = useMemo(
    () => getFilteredTableData(crossInvoicesData, selectedMonthsTable, null),
    [crossInvoicesData, selectedMonthsTable],
  );

  if (loading) return <CenteredCircularProgress />;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12}>
        <PageHeader.TitleOnly
          iconType="crossInvoice"
          title="Facturas Cruzadas"
          subtitle="Acá encontrarás las facturas que esta empresa ha emitido y recibido con sus relacionados."
        />
      </Grid>

      <Grid item xs={12} sx={{ pt: 0 }}>
        <TableCard sx={{ minHeight: 400 }}>
          <SubtitleWithTooltip
            title={`Evolución ${selectedPeriod.toLowerCase()}`}
            tooltipContent={`Serie mostrada: Monto cruzado total, Monto cruzado V y Monto cruzado C.`}
            tooltipPlacement="right"
            variant="compact"
          >
            <SelectInput<PeriodOption>
              label="Período"
              value={selectedPeriod}
              onChange={(v) => setSelectedPeriod(v ?? DEFAULT_PERIOD)}
              options={PERIOD_OPTIONS}
              size="small"
              fullWidth
            />
          </SubtitleWithTooltip>
          {filteredChartData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No hay datos disponibles para el período seleccionado.
            </Typography>
          ) : (
            <LineChart
              data={
                filteredChartData as unknown as {
                  name: string;
                  [key: string]: string | number;
                }[]
              }
              colors={CHART_SERIES_COLORS}
              height={290}
              showLegend={true}
              showArea={false}
              yAxisFormat="M"
            />
          )}
        </TableCard>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label="Total emitido"
              value={formatMoney(
                crossInvoicesData.totalEmittedAmount ??
                  totals.totalEmittedAmount,
              )}
              valueLabel="CLP"
              color={BASE_COLORS.orange}
              icon={<Receipt sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label="Total recibido"
              value={formatMoney(
                crossInvoicesData.totalRecievedAmount ??
                  totals.totalReceivedAmount,
              )}
              valueLabel="CLP"
              color={BASE_COLORS.green}
              icon={<Receipt sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label="Porcentaje recibido sobre emitido"
              value={
                crossInvoicesData.totalReceivedOverEmittedPercent != null
                  ? `${crossInvoicesData.totalReceivedOverEmittedPercent}%`
                  : '—'
              }
              valueLabel=""
              color={BASE_COLORS.blue}
              icon={<TrendingUp sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label="Total cruzado (prom.)"
              value={formatMoney(totals.averageCrossAmount)}
              valueLabel="CLP"
              color={BASE_COLORS.teal}
              icon={<TrendingUp sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label="Facturas emitidas (prom.)"
              value={formatMoney(totals.averageEmittedAmount)}
              valueLabel="CLP"
              color={BASE_COLORS.purple}
              icon={<TrendingUp sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              label="Facturas recibidas (prom.)"
              value={formatMoney(totals.averageReceivedAmount)}
              valueLabel="CLP"
              color={BASE_COLORS.darkGreen}
              icon={<TrendingUp sx={{ fontSize: 24 }} />}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title={`Buscar por empresa`}
            tooltipContent="Filtra la tabla por empresa y meses."
            tooltipPlacement="right"
            variant="compact"
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} lg={6} sx={{ minWidth: 280 }}>
                <AutocompleteInput<CrossInvoiceItem>
                  label="Empresa"
                  options={availableBusinesses}
                  value={selectedBusinessFilter}
                  getOptionLabel={(option) =>
                    `${option.name} - (${option.identifier})`
                  }
                  onChange={handleChangeBusiness}
                  size="small"
                  clearable
                />
              </Grid>
              <Grid item xs={12} lg={6} sx={{ minWidth: 280 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {useMonthShortcuts ? (
                      <SelectInput<MonthTableShortcut>
                        label="Meses"
                        value={monthTableShortcut}
                        onChange={(v) => setMonthTableShortcut(v ?? '')}
                        options={MONTH_TABLE_OPTIONS}
                        size="small"
                      />
                    ) : (
                      <MultiSelectCheckbox
                        options={availableMonths}
                        selectedValues={selectedMonthsManual}
                        onSelectionChange={setSelectedMonthsManual}
                        placeholder="Seleccionar meses"
                        size="small"
                        minWidth="100%"
                      />
                    )}
                  </Box>
                  <LightTooltip
                    title={
                      <Typography variant="body1" padding={1}>
                        {useMonthShortcuts
                          ? 'Cambiar a selección manual de meses'
                          : 'Cambiar a atajos de meses'}
                      </Typography>
                    }
                  >
                    <IconButton
                      onClick={handleToggleMonthMode}
                      color={useMonthShortcuts ? 'primary' : 'default'}
                      size="small"
                      sx={{
                        mt: 0.5,
                        padding: 0.5,
                        '& .MuiSvgIcon-root': { fontSize: 20 },
                      }}
                      aria-label={
                        useMonthShortcuts
                          ? 'Cambiar a selección manual de meses'
                          : 'Cambiar a atajos de meses'
                      }
                    >
                      <EventRepeatIcon />
                    </IconButton>
                  </LightTooltip>
                </Box>
              </Grid>
            </Grid>
          </SubtitleWithTooltip>

          <Box sx={{ mt: 2 }}>
            <EnhancedTable
              finalSumRow
              exportToFile="xlsx"
              tableSize="small"
              rowsPerPageDefault={10}
              dataHeaders={[
                { key: 'name', label: 'Razon Social' },
                { key: 'identifier', label: 'Rut' },
                { key: 'emittedCount', label: 'Facturas emitidas' },
                { key: 'emittedAmount', label: 'Monto emitido', type: 'money' },
                { key: 'recievedCount', label: 'Facturas recibidas' },
                {
                  key: 'recievedAmount',
                  label: 'Monto recibido',
                  type: 'money',
                },
                {
                  key: 'receivedOverEmittedPercent',
                  label: '% Recibido/emitido',
                  customComponent: (value: number | null | undefined) =>
                    value != null ? `${value}%` : '—',
                },
              ]}
              data={
                Array.isArray(filteredBusinessTable)
                  ? filteredBusinessTable
                  : []
              }
            />
          </Box>
        </TableCard>
      </Grid>
    </Grid>
  );
};

export default BusinessCrossInvoices;
